import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'

const labelStyle = { fontSize: 11, color: '#607d8b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }
const sectionStyle = { background: '#fff', borderRadius: 6, padding: 14, marginBottom: 12, border: '1px solid #e0e8eb' }

const fmtDate = (val) => {
  if (!val) return '—'
  try {
    const d = new Date(val)
    if (isNaN(d.getTime())) return '—'
    return d.toLocaleDateString()
  } catch (e) {
    return '—'
  }
}

function SprayCompletionTestPage({ url }) {
  const [ops, setOps] = useState([])
  const [loading, setLoading] = useState(false)
  const [busyOpId, setBusyOpId] = useState(null)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  const base = url || ''

  const loadOps = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await axios.get(`${base}/api/spray-completion-test/ops`)
      setOps(Array.isArray(res.data?.ops) ? res.data.ops : [])
    } catch (err) {
      console.error('[SprayCompletionTest] loadOps error', err)
      setError(err.response?.data?.message || err.message || 'Failed to load ops')
    } finally {
      setLoading(false)
    }
  }, [base])

  useEffect(() => { loadOps() }, [loadOps])

  const complete = async (opId) => {
    setBusyOpId(opId)
    setError(null)
    try {
      const res = await axios.post(`${base}/api/spray-completion-test/complete`, {
        opId
      })
      setResult(res.data || null)
      await loadOps()
    } catch (err) {
      console.error('[SprayCompletionTest] complete error', err)
      setError(err.response?.data?.message || err.message || 'Failed to complete op')
    } finally {
      setBusyOpId(null)
    }
  }

  const deleteInvoice = async (opId) => {
    setBusyOpId(opId)
    setError(null)
    try {
      await axios.delete(`${base}/api/spray-completion-test/invoice/${opId}`)
      await loadOps()
    } catch (err) {
      console.error('[SprayCompletionTest] deleteInvoice error', err)
      setError(err.response?.data?.message || err.message || 'Failed to delete invoice')
    } finally {
      setBusyOpId(null)
    }
  }

  const completion = result?.completion
  const receipt = result?.receipt

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', background: '#f5f7f8', overflow: 'hidden' }}>
      {/* LEFT — controls + ops list */}
      <div style={{ flex: '0 0 520px', overflowY: 'auto', padding: 16, borderRight: '1px solid #e0e8eb' }}>
        <h2 style={{ marginTop: 0, marginBottom: 4, fontSize: 18 }}>Spray Completion Test Harness</h2>
        <div style={{ fontSize: 12, color: '#607d8b', marginBottom: 16 }}>
          List completable spray ops, run the real invoice + Aspire receipt push, and delete a test
          invoice to re-run.
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button
            onClick={loadOps}
            disabled={loading}
            style={{
              padding: '8px 14px', fontSize: 12, fontWeight: 700,
              color: loading ? '#90a4ae' : '#263238',
              background: '#fff', border: '1px solid #cfd9dc', borderRadius: 4,
              cursor: loading ? 'wait' : 'pointer'
            }}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div style={{ marginBottom: 12, padding: 10, background: '#ffe8e8', color: '#c62828', borderRadius: 4, fontSize: 12 }}>
            {error}
          </div>
        )}

        {!loading && ops.length === 0 && (
          <div style={{ fontSize: 13, color: '#90a4ae', padding: 20, textAlign: 'center' }}>
            No completable spray ops found.
          </div>
        )}

        {ops.map((op) => {
          const rowBusy = busyOpId === op.opId
          return (
            <div key={op.opId} style={sectionStyle}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#263238' }}>{op.siteName || '(no site name)'}</div>
              <div style={{ fontSize: 12, color: '#607d8b', marginTop: 2 }}>{op.address || '—'}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8, fontSize: 12, color: '#455a64' }}>
                <span><span style={labelStyle}>Submitted</span>{fmtDate(op.submittedAt)}</span>
                <span><span style={labelStyle}>Acreage</span>{op.acreage != null ? op.acreage : '—'}</span>
                <span><span style={labelStyle}>Vendor</span>{op.vendorName || '—'}</span>
              </div>

              {op.hasInvoice && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#0d47a1', background: '#e3f2fd', border: '1px solid #b6d4f5', borderRadius: 12, padding: '3px 10px' }}>
                    {op.invoiceNumber || '(no #)'} ({op.invoiceStatus || 'unknown'})
                  </span>
                  <button
                    onClick={() => deleteInvoice(op.opId)}
                    disabled={rowBusy}
                    style={{
                      padding: '4px 10px', fontSize: 11, color: '#c62828',
                      background: 'transparent', border: '1px solid #e8c0c0', borderRadius: 3,
                      cursor: rowBusy ? 'wait' : 'pointer'
                    }}
                  >
                    Delete test invoice &amp; retry
                  </button>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button
                  onClick={() => complete(op.opId)}
                  disabled={rowBusy}
                  style={{
                    padding: '8px 16px', fontSize: 13, fontWeight: 700, color: '#fff',
                    background: rowBusy ? '#90a4ae' : '#558b6e',
                    border: 'none', borderRadius: 4, cursor: rowBusy ? 'wait' : 'pointer'
                  }}
                >
                  {rowBusy ? 'Completing…' : 'Complete'}
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* RIGHT — result panel */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 16, color: '#263238' }}>Latest result</h3>

        {!result && (
          <div style={{ fontSize: 14, color: '#90a4ae', padding: 20, textAlign: 'center' }}>
            Click "Complete" on an op to run the pipeline.
          </div>
        )}

        {result && completion == null && (
          <div style={sectionStyle}>
            <div style={{ fontSize: 13, color: '#455a64' }}>
              {result.message || 'No completion produced (completion was null).'}
            </div>
          </div>
        )}

        {result && completion != null && (
          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#263238' }}>
              {completion.deduped ? 'Deduped (existing invoice reused)' : 'Created new invoice'}
            </div>
            {completion.invoice && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13, color: '#455a64', marginBottom: 12 }}>
                <span><span style={labelStyle}>Invoice #</span>{completion.invoice.invoiceNumber || '—'}</span>
                <span><span style={labelStyle}>Amount</span>{completion.invoice.amount != null ? completion.invoice.amount : '—'}</span>
                <span><span style={labelStyle}>Acreage</span>{completion.invoice.acreage != null ? completion.invoice.acreage : '—'}</span>
                <span><span style={labelStyle}>Status</span>{completion.invoice.status || '—'}</span>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {completion.ourPdfUrl && (
                <a href={completion.ourPdfUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#558b6e' }}>Our PDF</a>
              )}
              {completion.subPdfUrl && (
                <a href={completion.subPdfUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#558b6e' }}>Sub PDF</a>
              )}
              {completion.invoicePdfUrl && (
                <a href={completion.invoicePdfUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#558b6e' }}>Invoice PDF</a>
              )}
            </div>
          </div>
        )}

        {result && receipt && (
          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#263238' }}>Aspire receipt</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13, color: '#455a64' }}>
              <span><span style={labelStyle}>Success</span>{String(!!receipt.success)}</span>
              <span><span style={labelStyle}>Receipt ID</span>{receipt.receiptId || '—'}</span>
              {receipt.status != null && (
                <span><span style={labelStyle}>Status</span>{receipt.status}</span>
              )}
            </div>
            {receipt.attachmentError && (
              <div style={{ marginTop: 10, padding: 8, background: '#ffe8e8', color: '#c62828', borderRadius: 4, fontSize: 12 }}>
                Attachment error: {receipt.attachmentError}
              </div>
            )}
            {receipt.error && (
              <div style={{ marginTop: 10, padding: 8, background: '#ffe8e8', color: '#c62828', borderRadius: 4, fontSize: 12 }}>
                Receipt error: {receipt.error}
              </div>
            )}
          </div>
        )}

        {result && (
          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8, color: '#607d8b' }}>Raw result</div>
            <pre style={{ margin: 0, fontSize: 11, color: '#37474f', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(SprayCompletionTestPage)
